// src/screens/KioskAppointmentConfirmationScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKioskDraft } from '../context/KioskDraftContext';
import { AuthContext } from '../context/AuthContext';
import { vehiclesAPI, kioskAPI } from '../services/api';
import AppHeader from '../components/AppHeader';
import CodeEntry from '../components/kiosk/CodeEntry';
import WaiverAcceptance from '../components/WaiverAcceptance';
import useKioskInactivity from '../hooks/useKioskInactivity';

const KioskAppointmentConfirmationScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { draft, clearDraft } = useKioskDraft();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Set once the code email is sent; the appointment exists only after the code is entered.
  const [verificationId, setVerificationId] = useState(null);
  // The customer accepts the waiver on the tablet before the code is sent.
  const [waiverVersion, setWaiverVersion] = useState(null);
  const [waiverKey, setWaiverKey] = useState(0);
  useKioskInactivity();

  const { vehicleId, customerName, customerEmail, customerPhone, date, time } = draft;

  useEffect(() => {
    if (!user || user.role !== 'kiosk') {
      navigate('/login');
      return;
    }

    if (!vehicleId || !customerName || !customerEmail || !customerPhone || !date || !time) {
      navigate('/kiosk/book');
      return;
    }
  }, [user, vehicleId, customerName, customerEmail, customerPhone, date, time, navigate]);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoading(true);
        const { data } = await vehiclesAPI.getOne(vehicleId);
        setVehicle(data);
      } catch (err) {
        console.error('Error fetching vehicle:', err);
        setError('Failed to load vehicle information');
      } finally {
        setLoading(false);
      }
    };

    if (vehicleId) {
      fetchVehicle();
    }
  }, [vehicleId]);

  // Step 1: send the customer a 6-digit code (every walk-in confirms by email).
  const handleConfirmAppointment = async () => {
    try {
      setSubmitting(true);
      setError('');

      const { data } = await kioskAPI.createAppointment({
        customerName,
        customerEmail,
        customerPhone,
        vehicles: [vehicleId],
        date,
        time,
        waiver: { accepted: true, version: waiverVersion },
      });
      setVerificationId(data.verificationId);
    } catch (err) {
      console.error('Error starting walk-in:', err);
      setError(err.response?.data?.message || 'Failed to create appointment');
      if (err.response?.data?.code === 'WAIVER_OUTDATED') {
        setWaiverKey((k) => k + 1); // show the new waiver; it must be accepted again
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: the code from the email creates the appointment.
  const handleVerifyCode = async (code) => {
    try {
      setSubmitting(true);
      setError('');
      const { data } = await kioskAPI.verifyAppointment(verificationId, code);
      clearDraft(); // booked: the customer's details are no longer needed here
      navigate(`/kiosk/book/success?appointmentId=${data._id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'That code did not work.');
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Confirm Appointment" />
        <div className="flex-1 flex items-center justify-center text-app-text-secondary-light dark:text-app-text-secondary text-sm">
          Loading...
        </div>
      </div>
    );
  }

  if (verificationId) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Confirm Appointment" subtitle="Enter the code we emailed you" />
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <CodeEntry
            email={customerEmail}
            onSubmit={handleVerifyCode}
            onResend={handleConfirmAppointment}
            submitting={submitting}
            error={error}
          />
          <button
            onClick={() => navigate('/kiosk', { replace: true })}
            className="w-full py-3 rounded-2xl text-sm text-app-text-secondary-light dark:text-app-text-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Confirm Appointment" subtitle="Review and confirm details" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {/* Customer Info */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
          <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
            Customer Information
          </div>
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Name</div>
          <div className="text-sm text-app-text-light dark:text-app-text">{customerName}</div>
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-2">Email</div>
          <div className="text-sm text-app-text-light dark:text-app-text">{customerEmail}</div>
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-2">Phone</div>
          <div className="text-sm text-app-text-light dark:text-app-text">{customerPhone}</div>
        </div>

        {/* Vehicle Info */}
        {vehicle && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-3">
              Vehicle
            </div>
            <div className="flex gap-3">
              <img
                src={vehicle.image}
                alt={`${vehicle.make} ${vehicle.model}`}
                className="w-24 h-20 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </div>
                {vehicle.type && (
                  <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                    {vehicle.type}
                  </div>
                )}
                {vehicle.price != null && (
                  <div className="text-sm text-app-accent mt-1 font-semibold">
                    ${vehicle.price.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Date & Time */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
          <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
            Date & Time
          </div>
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Date</div>
          <div className="text-sm text-app-text-light dark:text-app-text">{formatDate(date)}</div>
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-2">Time</div>
          <div className="text-sm text-app-text-light dark:text-app-text">{time}</div>
        </div>

        {/* Dealership Info */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
          <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
            Dealership
          </div>
          <div className="text-sm text-app-text-light dark:text-app-text">
            {user?.dealer?.name || 'N/A'}
          </div>
          {user?.dealer?.address && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-2">
              {user.dealer.address.street && <div>{user.dealer.address.street}</div>}
              {(user.dealer.address.city || user.dealer.address.state) && (
                <div>
                  {user.dealer.address.city && `${user.dealer.address.city}, `}
                  {user.dealer.address.state} {user.dealer.address.zip}
                </div>
              )}
            </div>
          )}
        </div>

        <WaiverAcceptance key={waiverKey} onChange={setWaiverVersion} large />
      </div>

      <div className="p-4 border-t border-app-border-light dark:border-app-border">
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            disabled={submitting}
            className="flex-1 py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            Back
          </button>

          <button
            onClick={handleConfirmAppointment}
            disabled={submitting || !waiverVersion}
            className="flex-1 py-3 rounded-2xl bg-app-accent text-black text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Sending code...' : 'Confirm Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KioskAppointmentConfirmationScreen;
